import type { AnimationBreakPoint, PanelChangeAnimation } from '@lighthouse/core'
import type { FC, MutableRefObject, PropsWithChildren } from 'react'
import React, { createContext, useContext } from 'react'

import { useCustomViewBlockContext } from './CustomViewContext'

export type ContainerBlockContextType = {
    scope?: string
    animation?: AnimationBreakPoint
    /**
     * Container-level storage for layerInView "once" semantics.
     * This avoids resetting once state when the active panel component is unmounted/remounted.
     */
    layerInViewOnce?: {
        /** which blockId should use this once-store (container blockId) */
        ownerBlockId: string
        key: string
        mapRef: MutableRefObject<Map<string, boolean>>
    }
    parentPanelSmartAnimation?: {
        containerId: string
        animation: PanelChangeAnimation
    }
}

export const ContainerBlockContext = createContext<ContainerBlockContextType>({})
export const ContainerBlockProvider: FC<
    PropsWithChildren<{
        value: ContainerBlockContextType
    }>
> = props => <ContainerBlockContext.Provider {...props} />
export const useContainerBlockContext = () => useContext(ContainerBlockContext)

/**
 * 计算有效的 anchor id：
 * - 自定义视图下（有 record）：id 为 `${anchorId}_${recordIndexPath}`，保证每个实例唯一
 * - scope 有值但非自定义视图：不设置 id，避免 DOM 重复
 * - 其他：使用 anchorId
 */
export const useEffectiveAnchorId = (anchorId: string | undefined): string | undefined => {
    const { scope } = useContainerBlockContext()
    const { record, recordIndexPath } = useCustomViewBlockContext()

    if (!anchorId) return undefined
    // 自定义视图下：使用 anchorId_recordIndexPath
    if (record != null && recordIndexPath !== undefined) {
        return `${anchorId}_${recordIndexPath}`
    }
    // scope 有值（重复上下文但非自定义视图）：不设置 id
    if (scope) return undefined
    return anchorId
}

export type PanelContextType = {
    currentPanel: string
    hasSmartAnimationParentPanel: string
}
export const PanelContext = createContext({ currentPanel: '', hasSmartAnimationParentPanel: '' })
export const PanelProvider: FC<PropsWithChildren<{ value: PanelContextType }>> = props => <PanelContext.Provider {...props} />
export const usePanelContext = () => useContext(PanelContext)
